import React, { useState, useEffect } from 'react';
import "../styles/page.scss";

const apiBasePath = process.env.REACT_APP_PATIENT_API_BASE_PATH;

// Map of groups to URLs
const GROUP_URLS = {
    EmpowerStaging: 'stg-empower-staging.kinsta.cloud',
    MyHeartCoachStaging: 'stg-wellnessappmyheartcoach-staging.kinsta.cloud',
    CirrhosisStage: 'stg-wellnessappstrive-staging.kinsta.cloud',
    Prehab4meStaging: 'staging-wellnesstoolbox.kinsta.cloud',
    Empower: 'empower.wellnesstoolbox.ca',
    Prehab4All: 'prehab4all.wellnesstoolbox.ca',
    Opal: 'opal.wellnesstoolbox.ca',
    Strive: 'strive.wellnesstoolbox.ca',
    MyHeartCoach: 'myheartcoach.wellnesstoolbox.ca',
    Peace: 'peace.wellnesstoolbox.ca'
};

const MagicLinkRedirect = () => {
    const [error, setError] = useState(null);
    const [group, setGroup] = useState(null);
    const [shortCode, setShortCode] = useState(null);
    const [redirectUrl, setRedirectUrl] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        const group = params.get('group');
        const source = params.get('source');
        const shortCode = params.get('s');
        const redirectUrlParam = params.get('redirectUrl');

        setGroup(group);
        setShortCode(shortCode);
        setRedirectUrl(redirectUrlParam ? decodeURIComponent(redirectUrlParam) : null);

        if (!token) {
            setError('Invalid magic link.');
            return;
        }

        const isDesktopUser = isDesktop();

        if (isDesktopUser && source === "browser") {
            openFlutterDesktopApp(token, group, shortCode, redirectUrlParam);
        } else {
            handleMagicLinkRedirect(token, group, shortCode, redirectUrlParam);
        }
    }, []);

    const handleMagicLinkRedirect = async (token, group, shortCode, redirectUrl) => {
        try {
            const response = await fetch(`${apiBasePath}/verifymagiclink`, {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token, group, shortCode }),
            });

            const data = await response.json();

            if (response.ok && data.hmacToken) {
                const hmacToken = data.hmacToken;
                const programUrl = GROUP_URLS[group];

                if (!programUrl) {
                    setError('Unknown group.');
                    return;
                }

                let webUrl = `https://${programUrl}/?waa=${encodeURIComponent(hmacToken)}`;
                if (redirectUrl) webUrl += `&redirectUrl=${encodeURIComponent(redirectUrl)}`;

                // Immediate redirect
                window.location.replace(webUrl);
            } else {
                setError('Your verification link has timed out. Please try logging in again.');
            }
        } catch (err) {
            console.error('Error:', err);
            setError('Your verification link has timed out. Please try logging in again.');
        }
    };

    function isDesktop() {
        const ua = navigator.userAgent.toLowerCase();
        const isMobile = /android|iphone|ipod|blackberry|webos|opera mini|windows phone|mobile/.test(ua);
        const isTablet = /ipad|android(?!.*mobile)/.test(ua);
        const smallScreen = window.innerWidth < 1024;
        return !isMobile && !isTablet && !smallScreen;
    }

    function openFlutterDesktopApp(token, group, shortCode, redirectUrl) {
        // Build the web login URL
        const params = new URLSearchParams({
            group,
            token,
            s: shortCode,
            source: 'app',
        });
        if (redirectUrl) params.set('redirectUrl', redirectUrl);

        const loginUrl = `${process.env.REACT_APP_LOGIN_URL}/magiclink?${params.toString()}`;

        // Encode the login URL for the deep link
        const appUrl = `wellnesstoolbox://open?loginUrl=${encodeURIComponent(loginUrl)}`;

        // Try to open the desktop app
        window.location.href = appUrl;

        // Fallback to web login after a short delay
        setTimeout(() => window.location.replace(loginUrl), 100);
    }

    return (
        <div className="loader-container">
            {error ? (
                <div className="error-message">
                    <p>{error}</p>
                    {group && (
                        <a
                            target="_blank"
                            rel="noreferrer"
                            href={`${process.env.REACT_APP_LOGIN_URL}/?group=${group}`}
                            style={{ marginTop: '1rem', display: 'inline-block', color: '#007bff', textDecoration: 'underline' }}
                        >
                            Click here to Login
                        </a>
                    )}
                </div>
            ) : group === 'Strive' || group === 'CirrhosisStage' ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    background: '#f4f6f9'
                }}>
                    <style>{`
                        .strive-loader {
                            background: #ffffff;
                            border-radius: 32px;
                            padding: 44px 32px 38px;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            width: 320px;
                            box-shadow: 0 4px 24px rgba(30, 78, 154, 0.08);
                        }
                        .strive-wordmark {
                            display: flex;
                            align-items: center;
                            gap: 6px;
                            margin-bottom: 36px;
                            animation: pulse 2s ease-in-out infinite;
                        }
                        .strive-wordmark span {
                            font-size: 28px;
                            font-weight: 700;
                            color: #1e4e9a;
                            letter-spacing: 0.04em;
                        }
                        .strive-wordmark svg {
                            animation: heartbeat 2.2s ease-in-out infinite;
                            transform-origin: left center;
                        }
                        .strive-triangles {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            gap: 6px;
                            margin-bottom: 32px;
                        }
                        .strive-triangles svg {
                            overflow: visible;
                        }
                        .strive-tagline {
                            display: flex;
                            align-items: center;
                            gap: 4px;
                            font-size: 13px;
                            font-weight: 700;
                            color: #1e4e9a;
                            letter-spacing: 0.12em;
                            text-transform: uppercase;
                        }
                        .strive-dot {
                            display: inline-block;
                            width: 5px;
                            height: 5px;
                            border-radius: 50%;
                            background: #00B4B4;
                            margin-left: 3px;
                        }
                        @keyframes pulse {
                            0%, 100% { opacity: 0.9; }
                            50%       { opacity: 1; }
                        }
                        @keyframes heartbeat {
                            0%,  100% { transform: scaleX(1); }
                            10%        { transform: scaleX(1.05); }
                            20%        { transform: scaleX(1); }
                            30%        { transform: scaleX(1.05); }
                            40%        { transform: scaleX(1); }
                        }
                        @keyframes bob {
                            0%, 100% { transform: translateY(0); }
                            50%       { transform: translateY(-10px); }
                        }
                        @keyframes armStretch {
                            0%, 100% { transform: scaleX(1); }
                            50%       { transform: scaleX(1.15); }
                        }
                        @keyframes steam {
                            0%, 100% { opacity: 0; transform: translateY(0); }
                            50%       { opacity: 0.7; transform: translateY(-5px); }
                        }
                        @keyframes laptopGlow {
                            0%, 100% { fill: #7ececa; }
                            50%       { fill: #00B4B4; }
                        }
                        @keyframes dotFade {
                            0%, 80%, 100% { opacity: 0.2; transform: scale(0.6); }
                            40%            { opacity: 1;   transform: scale(1); }
                        }
                        .tri-1 { animation: bob 1.4s 0.0s ease-in-out infinite; }
                        .tri-2 { animation: bob 1.4s 0.2s ease-in-out infinite; }
                        .tri-3 { animation: bob 1.4s 0.4s ease-in-out infinite; }
                        .arms   { animation: armStretch 1.4s 0.2s ease-in-out infinite; transform-origin: center; }
                        .steam-a { animation: steam 1.8s 0.0s ease-in-out infinite; }
                        .steam-b { animation: steam 1.8s 0.4s ease-in-out infinite; }
                        .laptop-screen { animation: laptopGlow 1.8s ease-in-out infinite; }
                        .dot-1 { animation: dotFade 1.4s 0.0s ease-in-out infinite; }
                        .dot-2 { animation: dotFade 1.4s 0.3s ease-in-out infinite; }
                        .dot-3 { animation: dotFade 1.4s 0.6s ease-in-out infinite; }
                    `}</style>
                    <div className="strive-loader">
                        <div className="strive-wordmark">
                            <span>STRIVE</span>
                            <svg width="48" height="20" viewBox="0 0 48 20">
                                <polyline
                                    points="0,10 8,10 12,2 16,18 20,10 28,10 32,6 36,14 40,10 48,10"
                                    fill="none"
                                    stroke="#00B4B4"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <div className="strive-triangles">
                            <svg className="tri-1" width="90" height="95" viewBox="0 0 90 95">
                                <polygon points="45,4 88,88 2,88" fill="#a8c4e0"/>
                                <circle cx="45" cy="35" r="7" fill="#1e4e9a"/>
                                <rect x="39" y="42" width="12" height="16" rx="4" fill="#1e4e9a"/>
                                <line x1="39" y1="50" x2="27" y2="46" stroke="#1e4e9a" strokeWidth="3.5" strokeLinecap="round"/>
                                <ellipse cx="24" cy="45" rx="7" ry="4" fill="#1e4e9a"/>
                                <ellipse cx="24" cy="43" rx="7" ry="2" fill="#2a6abf"/>
                                <path className="steam-a" d="M22 41 Q23 37 22 34" fill="none" stroke="#1e4e9a" strokeWidth="1.8" strokeLinecap="round"/>
                                <path className="steam-b" d="M26 41 Q27 37 26 34" fill="none" stroke="#1e4e9a" strokeWidth="1.8" strokeLinecap="round"/>
                                <line x1="42" y1="58" x2="37" y2="74" stroke="#1e4e9a" strokeWidth="4" strokeLinecap="round"/>
                                <line x1="48" y1="58" x2="53" y2="74" stroke="#1e4e9a" strokeWidth="4" strokeLinecap="round"/>
                            </svg>
                            <svg className="tri-2" width="90" height="95" viewBox="0 0 90 95">
                                <polygon points="45,91 2,7 88,7" fill="#f0b8cc"/>
                                <circle cx="45" cy="40" r="7" fill="#1e4e9a"/>
                                <rect x="39" y="47" width="12" height="16" rx="4" fill="#1e4e9a"/>
                                <g className="arms">
                                    <line x1="39" y1="53" x2="19" y2="53" stroke="#1e4e9a" strokeWidth="3.5" strokeLinecap="round"/>
                                    <line x1="51" y1="53" x2="71" y2="53" stroke="#1e4e9a" strokeWidth="3.5" strokeLinecap="round"/>
                                </g>
                                <line x1="42" y1="63" x2="37" y2="76" stroke="#1e4e9a" strokeWidth="4" strokeLinecap="round"/>
                                <line x1="48" y1="63" x2="53" y2="76" stroke="#1e4e9a" strokeWidth="4" strokeLinecap="round"/>
                            </svg>
                            <svg className="tri-3" width="90" height="95" viewBox="0 0 90 95">
                                <polygon points="45,4 88,88 2,88" fill="#90d4ce"/>
                                <circle cx="45" cy="35" r="7" fill="#1e4e9a"/>
                                <rect x="39" y="42" width="12" height="14" rx="4" fill="#1e4e9a"/>
                                <line x1="39" y1="50" x2="31" y2="55" stroke="#1e4e9a" strokeWidth="3.5" strokeLinecap="round"/>
                                <line x1="51" y1="50" x2="59" y2="55" stroke="#1e4e9a" strokeWidth="3.5" strokeLinecap="round"/>
                                <rect x="30" y="46" width="30" height="12" rx="2" fill="#1e4e9a"/>
                                <rect className="laptop-screen" x="32" y="48" width="26" height="8" rx="1.5"/>
                                <rect x="28" y="58" width="34" height="5" rx="2" fill="#1e4e9a"/>
                                <line x1="42" y1="63" x2="35" y2="76" stroke="#1e4e9a" strokeWidth="4" strokeLinecap="round"/>
                                <line x1="48" y1="63" x2="55" y2="76" stroke="#1e4e9a" strokeWidth="4" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <div className="strive-tagline">
                            <span>Keep Striving</span>
                            <span className="strive-dot dot-1"></span>
                            <span className="strive-dot dot-2"></span>
                            <span className="strive-dot dot-3"></span>
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div className="loader"></div>
                    <p style={{ marginTop: '1rem', textAlign: 'center' }}>
                        Built by experts, Made for you
                        <br />—thanks for showing up today :)
                    </p>
                </div>
            )}
        </div>
    );
};

export default MagicLinkRedirect;
